import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskService } from '../services/tasks';
import TaskCard from '../components/TaskCard';
import { t } from '../i18n/uiText';

function TaskList({ showToast, onLogout }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState('today');
  const [filter, setFilter] = useState('pending'); // 'all', 'completed', 'pending'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTasks();
  }, [view]);

  // Filter and sort tasks based on status and priority
  const getFilteredTasks = () => {
    let filteredTasks = [];
    
    if (filter === 'completed') {
      filteredTasks = tasks.filter(task => task.status === 'done');
    } else if (filter === 'pending') {
      filteredTasks = tasks.filter(task => task.status === 'todo');
    } else {
      filteredTasks = tasks;
    }
    
    // Sort by priority: high -> medium -> low -> no priority
    return filteredTasks.sort((a, b) => {
      const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1, '': 0 };
      const aPriority = priorityOrder[a.priority] || 0;
      const bPriority = priorityOrder[b.priority] || 0;
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority; // High priority first
      }
      
      // If same priority, sort by creation date (newest first)
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };


  const loadTasks = async () => {
    setLoading(true);
    try {
      let result;
      if (view === 'today') {
        result = await taskService.getTodayTasks();
      } else if (view === 'all') {
        result = await taskService.getAllTasks();
      } else if (view === 'templates') {
        result = await taskService.getTemplates();
      }
      setTasks(result);
    } catch (error) {
      showToast(`❌ ${t('taskLoadError')}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    
    // If it's a template, reset it instead of completing
    if (task?.isTemplate) {
      try {
        await taskService.resetTemplate(taskId);
        showToast(`🔄 ${t('taskTemplateResetDone')}`, 'success');
        loadTasks(); // Reload to show reset checklist
      } catch (error) {
        showToast(`❌ ${t('taskResetError')}`, 'error');
      }
      return;
    }
    
    // Regular task completion
    try {
      await taskService.completeTask(taskId);
      // Update task status to 'done' instead of removing it
      setTasks(tasks.map(t => 
        t.id === taskId ? { ...t, status: 'done' } : t
      ));
      showToast(`✅ ${t('taskDone')}`, 'success');
    } catch (error) {
      showToast(`❌ ${t('commonError')}`, 'error');
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await taskService.deleteTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
      showToast(`🗑️ ${t('taskDeleted')}`, 'success');
    } catch (error) {
      showToast(`❌ ${t('taskDeleteError')}`, 'error');
    }
  };


  return (
    <div className="min-h-screen pb-32">
      {/* Filter Bar - Fixed height to prevent jumping */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 h-16 flex items-center">
        {(view === 'today' || view === 'all') ? (
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                filter === 'pending'
                  ? 'bg-violet-100 text-violet-700 border border-violet-200'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              {t('taskFilterTodo')}
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                filter === 'completed'
                  ? 'bg-violet-100 text-violet-700 border border-violet-200'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              {t('taskFilterCompleted')}
            </button>
          </div>
        ) : (
          <div className="text-gray-500 text-sm">{t('taskTemplates')}</div>
        )}
      </div>

      {/* Task List */}
      <div className="p-2 space-y-1.5">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : getFilteredTasks().length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-4xl mb-2">📭</p>
            <p>{t('taskEmpty')}</p>
          </div>
        ) : (
          getFilteredTasks().map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => navigate(`/task/${task.id}`)}
              onComplete={handleComplete}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 px-3 pt-4 pb-8 flex gap-1 safe-area-inset-bottom">
        <button
          onClick={() => setView('today')}
          className={`flex-1 py-2 px-2 rounded-lg font-medium text-sm transition-all duration-200 active:scale-95 ${
            view === 'today'
              ? 'bg-white text-violet-600 shadow-md'
              : 'text-white/90 hover:text-white hover:bg-white/10'
          }`}
        >
          📅 {t('taskNavToday')}
        </button>
        <button
          onClick={() => setView('all')}
          className={`flex-1 py-2 px-2 rounded-lg font-medium text-sm transition-all duration-200 active:scale-95 ${
            view === 'all'
              ? 'bg-white text-violet-600 shadow-md'
              : 'text-white/90 hover:text-white hover:bg-white/10'
          }`}
        >
          📋 {t('taskNavAll')}
        </button>
        <button
          onClick={() => setView('templates')}
          className={`flex-1 py-2 px-2 rounded-lg font-medium text-sm transition-all duration-200 active:scale-95 ${
            view === 'templates'
              ? 'bg-white text-violet-600 shadow-md'
              : 'text-white/90 hover:text-white hover:bg-white/10'
          }`}
        >
          📋 {t('taskTemplates')}
        </button>
      </div>


    </div>
  );
}

export default TaskList;


